import { computed, ComputedRef } from 'vue'

import { SubParticipantPosition } from '@collector/sportsapi-client-legacy'
import { SubParticipant } from '@collector/sportsapi-types'
import { createIncidentPlayerOption } from '@desktop/views/Relation/Sports/composables/useIncidentPlayers/utils'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'
import { isSameSubparticipant } from '@desktop/views/Relation/utils'

import { sortPitcherIncidentOptions } from '../configuration/pitcherOrder'

/**
 * Player options for a given baseball side, including primary positions
 * (`pitcher`, `batter`) and secondary positions (`possible PIT`, `possible BAT`).
 *
 * Possible batters are pitchers with API `flags: ['possible_batter']` (mapped to
 * `hasSecondaryPosition`). Possible pitchers are batters with that same client flag
 * from local storage.
 *
 * Batter lists keep `lineup_order`. Pitcher lists use saved Pitchers-section order
 * (`pitcher_order`); SportsAPI leaves `lineup_order` null for pitchers.
 */
export function usePitcherBatterOptions(
  playerOptions: ComputedRef<IncidentPlayerOption[]>,
  currentPlayer: ComputedRef<SubParticipant | null>,
  positions: [SubParticipantPosition, SubParticipantPosition], // [primary, secondary]
): ComputedRef<IncidentPlayerOption[]> {
  function hasMissingPlayerInOptions(
    incidentPlayerOptions: IncidentPlayerOption[],
  ): boolean {
    if (!currentPlayer.value) {
      return false
    }

    const playerOption = incidentPlayerOptions.find((option) =>
      isSameSubparticipant(option.value, {
        id: currentPlayer.value!.id,
        shortName: currentPlayer.value!.short_name,
      }),
    )

    return !playerOption
  }

  const options = computed(() => {
    const incidentPlayerOptions: IncidentPlayerOption[] =
      playerOptions.value.filter(
        (option) =>
          option.metadata.position === positions[0] ||
          (option.metadata.position === positions[1] &&
            option.metadata.hasSecondaryPosition),
      )

    if (
      hasMissingPlayerInOptions(incidentPlayerOptions) &&
      currentPlayer.value
    ) {
      incidentPlayerOptions.push(
        createIncidentPlayerOption(currentPlayer.value),
      )
    }

    // Batters keep `playerOptions` order (`lineup_order`). Pitchers have null
    // `lineup_order`, so they are re-sorted to Pitchers-section add order —
    // including a missing current pitcher, which must be inserted before this
    // sort or it would sit after possible-pitcher batters.
    return positions[0] === SubParticipantPosition.Pitcher
      ? sortPitcherIncidentOptions(incidentPlayerOptions)
      : incidentPlayerOptions
  })

  return options
}
